import "./styles/ConfigSidebar.scss";
import React, {useEffect} from "react";
import Textfield from '@atlaskit/textfield';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Button, {IconButton} from '@atlaskit/button/new';
import Select from '@atlaskit/select';
import ColorPicker from "./ColorPicker";
import {confluenceService} from "../../api/ConfluenceService";
import {SelectOption} from "navigation-common/types/html-types";
import Toggle from "@atlaskit/toggle";
import Tooltip from "@atlaskit/tooltip";
import {Menu} from "navigation-common/types/navigation-types";
import {Slide} from "transitions-kit";
import {ErrorMessage, HelperMessage, Label} from "@atlaskit/form";
import styled from "styled-components";
import {SecurityService} from "navigation-common/SecurityService";
import {AsyncGroupSelect, GroupOption} from "./AsyncGroupSelect";
import {useAsyncFn} from "react-use";
import {errorFlag} from "../../model/flags";
import {useFlags} from "@atlaskit/flag";


interface ConfigSidebarProps {
    editMenuMetaData: Menu,
    groups: Array<string>,
    spaceLabels: Array<string>,
    allSpaceLabels: Array<SelectOption>,
    anonymousSettingDisabled: boolean,
    isDarkThemeMode?: boolean,
    closeTrigger: () => void,
    openSidebar: boolean,
    saveMenuMetaData: (menuMetaData: Menu) => Promise<void>,
    colorChangeTrigger: (color: string, darkTheme: boolean) => void,
    themeModeChangeTrigger?: (darkTheme: boolean) => void,
    subMenuBgColorChangeTrigger: (color: string, darkTheme: boolean) => void,
    groupChangeTrigger: (groups: Array<string>) => void,
    spaceLabelsChangeTrigger: (spaceLabels: Array<string>) => void,
    activeChangeTrigger: (active: boolean) => void,
    anonymousAccessChangeTrigger: (active: boolean) => void,
}

const ConfigSidebar = (props: ConfigSidebarProps) => {
    const [menuName, setMenuName] = React.useState<string>(props.editMenuMetaData.name);
    const [selectedGroups, setSelectedGroups] = React.useState<Array<GroupOption>>([]);
    const [selectedSpaceLabels, setSelectedSpaceLabels] = React.useState<Array<string>>([]);
    const [showDarkThemeColors, setShowDarkThemeColors] = React.useState<boolean>(props.isDarkThemeMode ?? false);
    const [isUpdatingMenu, setIsUpdatingMenu] = React.useState<boolean>(false);
    const [isAnonymousAccessSettingDisabled,] = React.useState<boolean>(props.anonymousSettingDisabled);
    const [nameFieldHasError, setNameFieldHasError] = React.useState<boolean>(false);
    const {showFlag} = useFlags();

    const [resolveGroupsState, resolveGroups] = useAsyncFn(async () => {
        const initGroups: GroupOption[] = [];

        try {
            for (const groupId of props.groups) {
                const group = await confluenceService.getGroupById(groupId);
                initGroups.push({value: group.id, label: group.name});
            }
        } catch (e) {
            handleOperationError("There was an error while fetching groups. Please try again later or contact support.");
        }

        setSelectedGroups(initGroups);
    }, [props.groups]);

    useEffect(() => {
        resolveGroups();
    }, [props.groups]);

    useEffect(() => {
        setMenuName(props.editMenuMetaData.name);
    }, [props.editMenuMetaData.name]);

    useEffect(() => {
        setSelectedSpaceLabels(props.spaceLabels);
    }, [props.groups, props.spaceLabels]);

    const handleOperationError = (error?: string) => {
        const errMsg = error ?? "There was an error. Please try again later or contact support.";
        showFlag(errorFlag({title: "Error!", description: errMsg}));
    }

    const saveSettingsAndCloseSidebar = async () => {
        setIsUpdatingMenu(true);
        await props.saveMenuMetaData(props.editMenuMetaData);
        setIsUpdatingMenu(false);
        closeSidebar();
    }

    const changeMenuName = (newName: string) => {
        setMenuName(newName);
        props.editMenuMetaData.name = newName;
    }

    const changeColor = (newColor: string) => {
        props.colorChangeTrigger(newColor, showDarkThemeColors);
    }

    const changeSubMenuBgColor = (newColor: string) => {
        props.subMenuBgColorChangeTrigger(newColor, showDarkThemeColors);
    }

    const changeGroups = (groups: any) => {
        setSelectedGroups(groups);
        props.groupChangeTrigger(groups?.length ? groups.map((group: SelectOption) => group.value) : []);
    }

    const changeSpaceLabels = (spaceLabels: Array<string>) => {
        setSelectedSpaceLabels(spaceLabels);
        props.spaceLabelsChangeTrigger(spaceLabels);
    };

    const closeSidebar = () => {
        props.closeTrigger();
    }

    const changeTheme = (selectedOption: any) => {
        const darkModeSelected = selectedOption.value === ThemeMode.DARK;
        setShowDarkThemeColors(darkModeSelected);
        if (props.themeModeChangeTrigger) {
            props.themeModeChangeTrigger(darkModeSelected);
        }
    }

    function validateName(nameValue: string) {
        const isValid = SecurityService.isValidName(nameValue)
        setNameFieldHasError(!isValid);
        return isValid;
    }

    return (
        <Slide direction={"left"} in={props.openSidebar} appear={props.openSidebar} mountOnEnter={true} unmountOnExit={true}>
            <div id={"sidebar"}>
                <div className={"sidebar-heading-wrapper"}>
                    <h2 className={"sidebar-heading"}>Menu Configuration</h2>
                    <IconButton icon={(iconProps) => (
                        <CrossIcon {...iconProps} size="small"/>
                    )} label={"close sidebar"} onClick={closeSidebar} appearance={"subtle"} spacing={"compact"}/>
                </div>
                <div className={"sidebar-item"}>
                    <h4 className={"sidebar-item-headline"}>Meta data</h4>
                    <Label htmlFor="textfield-name">Name of your menu</Label>
                    <Textfield id={"textfield-name"} value={menuName} isInvalid={nameFieldHasError}
                               onChange={(event) => {
                                   validateName(event.currentTarget.value)
                                   changeMenuName(event.currentTarget.value);
                               }}
                    />
                    {nameFieldHasError ? <ErrorMessage>Please do not use: &lt; &gt; \ &amp; % &#123; &#125; or empty</ErrorMessage> :
                        <HelperMessage>Please do not use: &lt; &gt; \ &amp; % &#123; &#125; or empty</HelperMessage>}
                </div>

                <div className={"sidebar-item"}>
                    <h4 className={"sidebar-item-headline"}>Visibility</h4>
                    <ToggleLabelWrapper>
                        <Label htmlFor="toggle-visibility">Show menu in Confluence</Label>
                        <Toggle id={"toggle-visibility"} isChecked={props.editMenuMetaData.active} onChange={(e) => {
                            props.activeChangeTrigger(e.target.checked);
                        }}/>
                    </ToggleLabelWrapper>
                    <div className={"meta"}>Hint: hide menu while editing to avoid displaying unfinished changes to the users.</div>
                </div>

                <div className={"sidebar-item"}>
                    <div className={"sidebar-item-headline headline-with-toggle"}>
                        <h4>{showDarkThemeColors ? "Dark Theme" : "Light Theme"}</h4>
                        <div className={"colorThemeContainer"}>
                            <Select spacing={"compact"}
                                    defaultValue={{
                                        value: props.isDarkThemeMode ? ThemeMode.DARK : ThemeMode.LIGHT,
                                        label: props.isDarkThemeMode ? ThemeMode.DARK : ThemeMode.LIGHT
                                    }}
                                    onChange={(val) => changeTheme(val)}
                                    options={[
                                        {value: ThemeMode.LIGHT, label: ThemeMode.LIGHT},
                                        {value: ThemeMode.DARK, label: ThemeMode.DARK}
                                    ]}
                            />
                        </div>
                    </div>
                    <div className={"sidebar-item"}>
                        <Label htmlFor="textfield-color-toplvl">Top level background</Label>
                        {showDarkThemeColors ? <ColorPicker color={props.editMenuMetaData.primaryColorDarkTheme ?? ""} changeHandler={changeColor}/> :
                            <ColorPicker color={props.editMenuMetaData.primaryColor} changeHandler={changeColor}/>}
                    </div>
                    <div className={"sidebar-item"}>
                        <Label htmlFor="textfield-color-sublvl">Dropdown background</Label>
                        {showDarkThemeColors ?
                            <ColorPicker color={props.editMenuMetaData.subMenuBgColorDarkTheme ?? ""} changeHandler={changeSubMenuBgColor}/> :
                            <ColorPicker color={props.editMenuMetaData.subMenuBgColor} changeHandler={changeSubMenuBgColor}/>}
                    </div>
                </div>

                <div className={"sidebar-item"}>
                    <h4 className={"sidebar-item-headline"}>Group assignment</h4>
                    <AsyncGroupSelect selectedGroups={selectedGroups} onChange={changeGroups} isLoading={resolveGroupsState.loading} label={"Show menu only to users in group(s)"}/>
                </div>

                <div className={"sidebar-item"}>
                    <ToggleLabelWrapper>
                        <Label htmlFor="toggle-anonymous-access">Display menu for anonymous users</Label>
                        <Tooltip
                            content={isAnonymousAccessSettingDisabled ? "There is already a menu for anonymous users. Deactivate the option in the other menu to enable it here." : ""}
                            position={"top"}>
                            <Toggle id={"toggle-anonymous-access"}
                                    isChecked={props.editMenuMetaData.anonymousAccess}
                                    isDisabled={isAnonymousAccessSettingDisabled}
                                    onChange={(event) => {
                                        props.anonymousAccessChangeTrigger(event.target.checked);
                                    }}
                            />
                        </Tooltip>
                    </ToggleLabelWrapper>
                    <div className={"meta"}>This option can only be selected for one menu</div>
                </div>

                <div className={"sidebar-item"}>
                    <h4 className={"sidebar-item-headline"}>Space category assignment</h4>
                    <div style={{marginBottom: "6px"}}>
                        <Label htmlFor="select-space-labels">Space category(s)</Label>
                        <Select
                            id={"select-space-labels"}
                            value={props.allSpaceLabels.filter(label => selectedSpaceLabels.includes(label.value))}
                            options={props.allSpaceLabels}
                            placeholder="Select space category(s)"
                            isMulti
                            onChange={(labels) => {
                                changeSpaceLabels(labels?.map(label => label.value) ?? []);
                            }}
                        /></div>
                    <div className={"meta"}>Show menu only in certain spaces by assigning space category(s)</div>
                </div>

                <Button isLoading={isUpdatingMenu} appearance={"primary"} onClick={saveSettingsAndCloseSidebar}>Confirm</Button>
            </div>
        </Slide>
    );
};

export default ConfigSidebar;

const ToggleLabelWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;

    label {
        margin-bottom: 0;
    }
`;

enum ThemeMode {
    LIGHT = "light",
    DARK = "dark"
}
